import { useTranslation } from "react-i18next";
import { api } from "@/comn";
import { comnUtils } from "@/comn/utils";
import { Grid, Group, Page } from "@/comn/components";
import { TFormSchema, TGridSchema, useFetch, useForm, useGrid, useResource } from "@/comn/hooks";

/* ------------------------------------------------------------------
 * 인터페이스 실패 목록 팝업
 * ------------------------------------------------------------------
 * 메인 화면 "인터페이스 실적 통계"(TanogaIntfStstcl)의 기관별 상세 표에서
 * 실패 건수를 누르면 modal.openModal({ content: <IntfFailList params={...} /> }) 로 열린다.
 * url(iframe) 방식은 높이가 90vh 로 고정돼 목록보다 커지므로 컴포넌트로 직접 띄운다.
 *  - props.params: { orgKey, orgNm, sndrcvDvn, strtDt, endDt }
 *  - 합계(Total Count) 행에서 열면 orgKey 가 빈 값으로 들어오고, 서버가 전 기관으로 조회한다.
 * 정렬(최초등록일시 최신순)은 서버가 맡으므로 화면에서 다시 정렬하지 않는다.
 * ------------------------------------------------------------------ */

/** 서버 API 경로. 통계 API 와 같은 OsciMainController 계열이다. */
const INTF_FAIL_LIST_API = "/api/v1/osci/comn/main/ststcl/intf-fail-list";

/** 송수신 구분. 호출하는 메인 화면과 같은 값(S/R)을 받는다. */
const SNDRCV_DVN = { RCVE: "R", SEND: "S" };

/**
 * 실패 목록 한 페이지 건수.
 * 기관·방향 조합에 실패가 2만 건을 넘는 경우가 있어 전체를 한 번에 받지 않는다.
 */
const FAIL_PAGE_SIZE = 10;

/** 전송상태 코드 그룹. 연계 이력 화면(OSCI0113001Q)과 같은 그룹을 쓴다. */
const TRSC_STAT_CD_GRP = "OSC0102";

/**
 * 조회 기간 표기용 폼 스키마.
 * 대시보드 달력(SF_INTF_STSTC_SRCH)과 같은 공통 daterange 컨트롤을 쓰되 edit: false 로 둬서 입력 없이 글자로만 그린다.
 * 보기 모드의 날짜 컨트롤은 언어별 날짜 형식으로 찍으므로(영어 DD/MM/YYYY) 다른 화면의 기간 표기와 같아진다.
 */
const SF_INTF_FAIL_COND: TFormSchema = {
    id: "form_intfFailCond",
    schema: {
        srchPrid: {
            type: "daterange",
            label: "L_PRID",
            start: { name: "srchStrtDt" },
            end: { name: "srchEndDt" },
            edit: false,
        },
    },
};

/**
 * 실패 목록 그리드 스키마.
 * pagination "out" 은 서버 페이징이다. 실패가 2만 건을 넘는 조합이 있어
 * 페이지를 옮길 때마다 서버를 다시 부른다.
 * 전송상태는 코드값으로 내려오므로 연계 이력 화면과 같은 코드 그룹을 붙이고,
 * viewType "label" 로 코드 없이 이름만 보여 준다.
 * 인터페이스 명은 70자에 이르는 값이 있어 남는 폭을 이 칸에 몰아 준다.
 */
const SG_INTF_FAIL_LIST: TGridSchema = {
    id: "grid",
    options: { pagination: "out", height: "auto" },
    head: [
        /* 로그 ID 는 32자 고정, 전송상태 이름과 일시도 한 줄에 들어가야 읽힌다.
           남는 폭은 길이가 들쭉날쭉한 인터페이스 명에 몰아 준다. */
        { cells: [{ header: "L_TRSC_LOG_ID", width: 290 }] },
        { cells: [{ header: "L_INTF_NM", width: "*" }] },
        { cells: [{ header: "L_TRSC_STAT", width: 230 }] },
        { cells: [{ header: "L_FRST_RGSR_DTM", width: 180 }] },
    ],
    body: [
        { cells: [{ binding: "trscLogId" }] },
        { cells: [{ binding: "intfNm", align: "left" }] },
        {
            cells: [
                {
                    binding: "trscStatCd",
                    type: "code",
                    area: "comnCd",
                    comnCd: TRSC_STAT_CD_GRP,
                    viewType: "label",
                },
            ],
        },
        { cells: [{ binding: "frstRgsrDtm", type: "datetime" }] },
    ],
};

export const IntfFailList = (props: any) => {
    const { t } = useTranslation();
    /* 조건이 빠져 있으면 빈 조건으로 두고 서버 검증에 맡긴다. */
    const params = props.params || {};
    const { orgKey, orgNm, sndrcvDvn, strtDt, endDt } = params;

    /* 전송상태 라벨용 코드 그룹. 메인 화면은 이 그룹을 불러 두지 않으므로 팝업이 직접 부른다. */
    useResource({
        defaultSchema: [{ area: "comnCd", comnCd: TRSC_STAT_CD_GRP }],
    });

    /* 기간은 대시보드가 넘긴 YYYY-MM-DD 값을 그대로 기본값으로 둔다. 읽기 전용이라 값이 바뀔 일은 없다. */
    const form = {
        failCond: useForm({
            defaultSchema: SF_INTF_FAIL_COND,
            defaultValues: { srchStrtDt: strtDt, srchEndDt: endDt },
        }),
    };

    const grid = {
        failList: useGrid({ defaultSchema: SG_INTF_FAIL_LIST, size: FAIL_PAGE_SIZE }),
    };

    const render = {
        cell: {
            /* 인터페이스 명 칸은 "[인터페이스 ID] 인터페이스 명" 으로 보여 준다.
               서버는 이름이 없으면 ID 로 대체해 내려주므로(COALESCE), 이름이 ID 와 같으면 ID 만 한 번 적는다. */
            intfNm: ({ value, rowValues }: any) => {
                const intfId = rowValues?.intfId;
                if (!intfId) return value;
                return !value || value === intfId ? `[${intfId}]` : `[${intfId}] ${value}`;
            },
        },
    };

    const fetch = {
        getIntfFailList: useFetch({
            api: (page = grid.failList.page) => {
                const base = process.env.REACT_APP_API_OSCI || "";
                return api.get(base + INTF_FAIL_LIST_API, {
                    params: comnUtils.toGetParams({
                        srchStrtDt: strtDt,
                        srchEndDt: endDt,
                        orgKey: orgKey,
                        sndrcvDvn: sndrcvDvn,
                        page: page,
                        size: grid.failList.size,
                    }),
                });
            },
            /* 팝업은 열리는 순간이 곧 조회 시점이라 별도 조회 조건을 두지 않는다. */
            enabled: true,
            key: [grid.failList.page, grid.failList.size],
            onError: (error) => {
                /* 조회에 실패하면 목록을 비운다. 다른 조건의 결과가 남아 있으면 안 된다. */
                console.log(error);
                grid.failList.setData({ content: [], page: { totalElements: 0 } });
            },
        }),
    };

    return (
        <Page title={t("T_INTF_FAIL_LIST")}>
            {/* 어떤 조건의 목록인지 팝업 안에서 다시 확인되게 둔다. 입력 없는 읽기 전용 표기다. */}
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Col label="L_ORG">{orgNm}</Group.Col>
                            <Group.Col label="L_SNDRCV_TP_NM">
                                {sndrcvDvn === SNDRCV_DVN.SEND ? t("L_SEND") : t("L_RCVE")}
                            </Group.Col>
                        </Group.Row>
                        <Group.Row>
                            {/* 기간은 공통 daterange 컨트롤의 보기 모드로 그려 앱의 날짜 형식을 따른다. */}
                            <Group.Control {...form.failCond.schema.srchPrid} controlSize={10} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Grid
                        {...grid.failList.grid}
                        data={fetch.getIntfFailList.data?.intfStstclFailList}
                        render={render}
                    />
                </Group.Body>
            </Group>
        </Page>
    );
};
